import Logo from "../shared/Logo";
import LangLink from "../shared/LangLink";
import LanguageSwitcher from "./LanguageSwitcher";
import { FOOTER_PROCEDURES, FOOTER_GUIDES, FOOTER_LEGAL, FOOTER_MAIN } from "../../data/navigation";
import { getLocalizedSlug } from "../../data/localizedRoutes";
import { DOCTOR_REVIEW } from "../../content/doctorReviewContent";
import { AFTERCARE } from "../../content/aftercareContent";
import { SITE, DISCLAIMER_LONG } from "../../data/site";
import { useI18n } from "../../i18n";
import { Mail, Phone, MessageCircle } from "lucide-react";

export default function Footer() {
  const { t, lang } = useI18n();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-primary text-primary-foreground font-sans" data-testid="site-footer">
      <div className="container-psv py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-4">
            <Logo variant="light" />
            <p className="mt-6 text-primary-foreground/70 text-[15px] leading-relaxed max-w-sm">
              {t("footer.description")}
            </p>
            <div className="mt-8 space-y-3 text-sm">
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 text-primary-foreground/80 hover:text-accent transition-colors" data-testid="footer-email">
                <Mail className="w-4 h-4 text-accent" /> {SITE.email}
              </a>
              <a href={SITE.phoneHref} className="flex items-center gap-3 text-primary-foreground/80 hover:text-accent transition-colors" data-testid="footer-phone">
                <Phone className="w-4 h-4 text-accent" /> {SITE.phone}
              </a>
              <a href={SITE.whatsappHref} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-primary-foreground/80 hover:text-accent transition-colors" data-testid="footer-whatsapp">
                <MessageCircle className="w-4 h-4 text-accent" /> WhatsApp
              </a>
            </div>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-2">
            <h4 className="text-accent text-xs uppercase tracking-[0.18em] font-semibold mb-5">{t("footer.procedures")}</h4>
            <ul className="space-y-3 text-sm">
              {FOOTER_PROCEDURES.map((l) => (
                <li key={l.to}>
                  <LangLink to={l.to} className="text-primary-foreground/75 hover:text-accent transition-colors">{l.label}</LangLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-accent text-xs uppercase tracking-[0.18em] font-semibold mb-5">{t("footer.guides")}</h4>
            <ul className="space-y-3 text-sm">
              {FOOTER_GUIDES.map((l) => (
                <li key={l.to}>
                  <LangLink to={l.to} className="text-primary-foreground/75 hover:text-accent transition-colors">{l.label}</LangLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-accent text-xs uppercase tracking-[0.18em] font-semibold mb-5">{SITE.name}</h4>
            <ul className="space-y-3 text-sm">
              {FOOTER_MAIN.map((l) => (
                <li key={l.key}>
                  <LangLink to={l.to} className="text-primary-foreground/75 hover:text-accent transition-colors">{t(l.label)}</LangLink>
                </li>
              ))}
              <li>
                <LangLink to={`/${getLocalizedSlug("doctorReview", lang)}`} className="text-primary-foreground/75 hover:text-accent transition-colors">{(DOCTOR_REVIEW[lang] || DOCTOR_REVIEW.en).home.cta}</LangLink>
              </li>
              <li>
                <LangLink to={`/${getLocalizedSlug("aftercare", lang)}`} className="text-primary-foreground/75 hover:text-accent transition-colors">{(AFTERCARE[lang] || AFTERCARE.en).home.cta}</LangLink>
              </li>
            </ul>
            <h4 className="text-accent text-xs uppercase tracking-[0.18em] font-semibold mt-8 mb-4">{t("footer.languages")}</h4>
            <LanguageSwitcher className="[&_a]:text-primary-foreground/80 [&_a:hover]:text-accent" />
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-16 pt-10 border-t border-primary-foreground/15">
          <p className="text-primary-foreground/60 text-[13px] leading-relaxed max-w-4xl" data-testid="footer-disclaimer">
            {DISCLAIMER_LONG}
          </p>
          <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-[13px] text-primary-foreground/60">
            <p>© {year} {SITE.name}. {t("footer.rights")}</p>
            <ul className="flex flex-wrap gap-x-6 gap-y-2">
              {FOOTER_LEGAL.map((l) => (
                <li key={l.to}>
                  <LangLink to={l.to} className="hover:text-accent transition-colors" data-testid={`footer-legal-${l.to.replace(/\//g, "")}`}>{l.label}</LangLink>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}
